import { anchorFromUrl } from '../types/xlsxAnchor';
import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AnalysisHistoryView from '@components/documents/AnalysisHistoryView';
import PermissionManagementView from '@components/documents/PermissionManagementView';
import VersionHistoryView from '@components/documents/VersionHistoryView';
import DocumentDetailHeader from '@components/documents/document-detail/DocumentDetailHeader';
import DocumentMetadataFields from '@components/documents/document-detail/DocumentMetadataFields';
import DocumentPreviewPanel from '@components/documents/document-detail/DocumentPreviewPanel';
import ExtractedImagesPanel from '@components/documents/document-detail/ExtractedImagesPanel';
import ImageComparisonDialog from '@components/documents/document-detail/ImageComparisonDialog';
import MetadataReExtractPanel from '@components/documents/document-detail/MetadataReExtractPanel';
import TaxonomyReviewPanel from '@components/documents/metadata/TaxonomyReviewPanel';
import { DOCUMENT_DETAIL_TABS, type DocumentDetailTabId } from '@constants/documentDetail';
import { useDocumentDetail } from '@hooks/useDocumentDetail';
import { useDocumentHighlightParams } from '@hooks/useDocumentHighlightParams';
import { useDocumentImages } from '@hooks/useDocumentImages';
import { useDocumentPreview } from '@hooks/useDocumentPreview';
import { useDocumentPreviewOptions } from '@hooks/useDocumentPreviewOptions';
import { useImageComparison } from '@hooks/useImageComparison';
import { useMetadataReExtraction } from '@hooks/useMetadataReExtraction';
import { useTaxonomyReview } from '@hooks/useTaxonomyReview';
import { documentService } from '@services/document.service';

const DocumentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const xlsxAnchor = useMemo(() => anchorFromUrl(location.pathname + location.search), [location.pathname, location.search]);
  const { t } = useTranslation();

  // The URL is the source of truth for the highlight target: keeping the params
  // means a refresh reproduces the view and the link can be shared. They used to
  // be copied into refs and deleted from the URL, which lost all three.
  const { highlightTerms, initialPage, initialPageExplicit, preferredSearchPage } =
    useDocumentHighlightParams();

  const [activeTab, setActiveTab] = useState<DocumentDetailTabId>('preview');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { doc, loading, error, patchDoc } = useDocumentDetail(id);
  const preview = useDocumentPreview({
    id, doc, isActive: activeTab === 'preview', ...useDocumentPreviewOptions(doc),
  });
  const imageState = useDocumentImages(id, activeTab === 'metadata');
  const comparison = useImageComparison(id, imageState.images);
  const extraction = useMetadataReExtraction(id, patchDoc);
  const taxonomyReview = useTaxonomyReview(id, activeTab === 'metadata');

  // Replace, so the highlight URL does not linger in the browser history.
  const handleBack = () => navigate('/documents', { replace: true });

  const handleDelete = async () => {
    if (!id || deleting) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await documentService.deleteDocument(id);
      navigate('/documents', { replace: true });
    } catch (_error) {
      setDeleteError(t('documents.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertDescription className="revy-cjk leading-[1.6]">
            {error || t('documents.documentNotFound')}
          </AlertDescription>
        </Alert>
        <Button variant="ghost" className="mt-4" onClick={handleBack}>
          <ArrowLeft strokeWidth={1.9} />
          <span className="revy-cjk">{t('documents.returnToDocuments')}</span>
        </Button>
      </div>
    );
  }

  return (
    <div>
      <DocumentDetailHeader
        doc={doc}
        onBack={handleBack}
        onNavigateToList={() => navigate('/documents')}
        onDelete={doc.permissions.canDelete ? () => setDeleteOpen(true) : undefined}
      />

      {/* Clips the tab rule to the card radius. */}
      <Card className="overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as DocumentDetailTabId)}
          // Radix activates on focus by default; MUI did not. Without this,
          // arrowing across the tabs selects each one in passing and kicks off
          // the image and preview fetches for every tab it lands on.
          activationMode="manual"
        >
          {/* Underlined rather than the stock pill list: the stock TabsList is a
              fixed-height nowrap group that cannot hold an icon above a
              Japanese label. */}
          <TabsList
            aria-label={t('documents.detailTabsAria')}
            className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b bg-transparent p-0"
          >
            {DOCUMENT_DETAIL_TABS.map(({ id: tabId, labelKey, icon: Icon }) => (
              <TabsTrigger
                key={tabId}
                value={tabId}
                className="flex-col gap-1 rounded-none border-b-2 border-transparent px-4 py-3 text-sm shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
              >
                <Icon className="size-5" strokeWidth={1.9} />
                <span className="revy-cjk whitespace-nowrap">{t(labelKey)}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Radix unmounts inactive content, matching the pre-port TabPanel. */}
          <TabsContent value="preview" className="mt-0 p-6">
            <DocumentPreviewPanel
              doc={doc}
              xlsxAnchor={xlsxAnchor}
              previewUrl={preview.previewUrl}
              previewLoading={preview.previewLoading}
              previewError={preview.previewError}
              onRetry={preview.reload}
              onUploadNewVersion={() => navigate('/documents/upload')}
              highlightTerms={highlightTerms}
              initialPage={initialPage}
              initialPageExplicit={initialPageExplicit}
              preferredSearchPage={preferredSearchPage}
              previewState={preview.previewState}
              previewContentType={preview.previewContentType}
              slideCount={preview.previewSlideCount}
            />
          </TabsContent>

          <TabsContent value="metadata" className="mt-0 p-6">
            <h2 className="revy-cjk mb-2 text-base font-medium leading-[1.5]">
              {t('documents.metadata')}
            </h2>
            <DocumentMetadataFields doc={doc} />
            <TaxonomyReviewPanel review={taxonomyReview} />
            <MetadataReExtractPanel extraction={extraction} />
            <ExtractedImagesPanel imageState={imageState} comparison={comparison} />
          </TabsContent>

          <TabsContent value="analysis" className="mt-0 p-6">
            <AnalysisHistoryView analysisHistory={doc.analysisHistory} />
          </TabsContent>

          <TabsContent value="versions" className="mt-0 p-6">
            <h2 className="revy-cjk mb-2 text-base font-medium leading-[1.5]">
              {t('documents.versionHistory')}
            </h2>
            <VersionHistoryView documentId={id} analysisHistory={doc.analysisHistory} />
          </TabsContent>

          <TabsContent value="permissions" className="mt-0 p-6">
            <h2 className="revy-cjk mb-2 text-base font-medium leading-[1.5]">
              {t('documents.documentPermissions')}
            </h2>
            <PermissionManagementView documentId={id} />
          </TabsContent>
        </Tabs>
      </Card>

      <ImageComparisonDialog comparison={comparison} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="revy-ui">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.deleteConfirm', { name: doc.title || doc.metadata?.fileName || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentDetailPage;
